/**
 * Side-effect barrel: importing this file ensures the *active* analytics
 * provider has registered itself into `registry.ts`. `dispatch.ts` and
 * `identify.ts` import this so callers don't have to think about init order.
 *
 * The exported `providersReady` Promise resolves once the active provider's
 * dynamic chunk has loaded and self-registered. Async facade functions in
 * `identify.ts` await it before calling `getActiveProvider()` — without this
 * gate, calls fired between module load and chunk arrival hit a null provider
 * and silently no-op.
 *
 * The branch is gated on the build-time `VITE_ANALYTICS_TOOL` constant so Vite
 * dead-code-eliminates the import — keeping `posthog-js` out of bundles for
 * brands with no analytics tool configured.
 */
import logger from "@utils/logger";

const ready: Promise<void> =
  import.meta.env.VITE_ANALYTICS_TOOL === "posthog" ? import("./posthog").then(() => undefined) : Promise.resolve();

export const providersReady: Promise<void> = ready.catch((error) => {
  logger._console.error("[analytics] failed to load provider chunk", error);
});

/** Defer `fn` until the active analytics provider has registered; events fired before then hit a null provider and no-op. */
export function runWhenProvidersReady(fn: () => void): void {
  void providersReady.then(fn);
}
